// Utils
// Constants
import { DEFAULT } from "@/constants/defaultValues";
import {
  AggSubPanelResults,
  AkroResultsData,
  RhythmicResultsData,
  GroupTotalData,
  JudgePointsMap,
  OtherJudgesAssessmentsResult,
} from "@/types/results.ts";

/**
 * @example
 * formatAssessmentToFloat("11,0"): 11
 * formatAssessmentToFloat("11,1"): 11.1
 * formatAssessmentToFloat("11.1"): 11.1
 */
export function formatAssessmentToFloat(value: string): number {
  const formattedValue = value.split("").includes(",") ? value.replace(",", ".") : value;
  return parseFloat(formattedValue);
}

/**
 * @example
 * formatAssessmentToString(10): "10"
 * formatAssessmentToString(2.3): "2,3"
 * formatAssessmentToString(1.111): "1,11"
 */
export function formatAssessmentToString(value: number | string): string {
  const valueString = String(value);
  const isFloat = valueString.includes(".");

  if (isFloat) {
    const commaIndex = valueString.indexOf(".");
    const primary = valueString.slice(0, commaIndex).slice(0, 2);
    const secondary = valueString.slice(commaIndex + 1).slice(0, 2);
    return `${primary},${secondary}`;
  }
  return valueString;
}

/**
 *
 * @example
 * formatNumberWithDecimals(10, 1)          // "10,0"
 * formatNumberWithDecimals("9,56", 1)      // "9,6"
 * formatNumberWithDecimals("9.56", 2)      // "9,56"
 */
export function formatNumberWithDecimals(
  value: number | string,
  decimals: number,
  trimTrailingZeros = false
): string {
  const str = String(value).trim();
  if (!str) {
    return "";
  }

  const num = Number(str.replace(",", "."));
  if (!Number.isFinite(num)) {
    return str;
  }

  let result = num.toFixed(decimals);

  if (trimTrailingZeros) {
    result = result.replace(/\.?0+$/, "");
  }

  return result.replace(".", ",");
}

function formatOtherJudgesAssessmentsResults(
  judgePoints: JudgePointsMap,
  invalidJudgePointsCodes: string[],
  isInputModeElements?: boolean
): OtherJudgesAssessmentsResult[] {
  return Object.values(judgePoints).map(({ judgeCode, values }) => {
    let assessment: string;

    if (isInputModeElements && values?.value) {
      assessment = Number(values.value).toFixed(2);
    } else if (values?.value) {
      assessment = formatAssessmentToString(Number(values.value));
    } else {
      assessment = DEFAULT.ASSESSMENT;
    }

    const warning = invalidJudgePointsCodes.includes(judgeCode as string);

    return {
      judgeCode: judgeCode as string,
      assessment,
      bonus: values?.bonus,
      penalti: values?.deductions,
      warning,
    };
  });
}

export interface JudgePointsData {
  invalidJudgePointsCodes: string[];
  judgePoints: JudgePointsMap;
  subPanelPoints?: Record<string, unknown>;
  total?: number;
  sumbitted?: boolean;
}

export interface FormattedJudgePointsData {
  total: string | null;
  results: OtherJudgesAssessmentsResult[];
  subPanelPoints?: Record<string, unknown>;
  sumbitted?: boolean;
}

export function formatOtherJudgesAssessments(
  {
    invalidJudgePointsCodes,
    judgePoints,
    subPanelPoints,
    total: totalRaw,
    sumbitted,
  }: JudgePointsData,
  isInputModeElements?: boolean
): FormattedJudgePointsData {
  const total = formatTotalPointToString(totalRaw);
  const results = formatOtherJudgesAssessmentsResults(
    judgePoints,
    invalidJudgePointsCodes,
    isInputModeElements
  );

  return {
    total,
    results,
    subPanelPoints,
    sumbitted,
  };
}

interface FormattedGroupTotal {
  invalidJudgePointsCodes: string[];
  judgePoints: Record<string, unknown>;
  total?: string;
}

function formatGroupTotal({
  invalidJudgePointsCodes,
  judgePoints: judgePointsRaw,
  total: totalRaw,
}: GroupTotalData): FormattedGroupTotal {
  const total = formatTotal(totalRaw);
  const judgePoints: Record<string, unknown> = {};

  Object.entries(judgePointsRaw).forEach(([key, properties]) => {
    const value = properties.values.value
      ? formatAssessmentToString(Number(properties.values.value))
      : DEFAULT.ASSESSMENT;

    const warning = invalidJudgePointsCodes.includes(properties.judgeCode as string);
    const values = {
      ...properties.values,
      value,
    };

    judgePoints[key] = {
      ...properties,
      warning,
      values,
    };
  });

  return {
    invalidJudgePointsCodes,
    judgePoints,
    total,
  };
}

const formatTotal = (totalRaw?: number): string | undefined =>
  totalRaw ? totalRaw.toFixed(3).replace(".", ",") : undefined;

export interface FormattedRhythmicResults {
  otherPoints: {
    deductions: string;
    judgePointsCorrections: Record<string, unknown>;
    version: number;
  };
  subPanelResults: {
    D_TOTAL: FormattedGroupTotal;
    A_TOTAL: FormattedGroupTotal;
    E_TOTAL: FormattedGroupTotal;
  };
  submitted?: boolean;
  total?: string;
}

export function formatOtherJudgesAssessmentsRhythmic({
  otherPoints: otherPointsRaw,
  subPanelResults: subPanelResultsRaw,
  submitted,
  total: totalRaw,
}: RhythmicResultsData): FormattedRhythmicResults {
  const otherPoints = {
    deductions: otherPointsRaw?.deductions
      ? formatAssessmentToString(otherPointsRaw?.deductions)
      : DEFAULT.ASSESSMENT,
    judgePointsCorrections: otherPointsRaw?.judgePointsCorrections || {},
    version: otherPointsRaw?.version || 0,
  };
  const total = formatTotal(totalRaw);

  return {
    otherPoints,
    subPanelResults: {
      D_TOTAL: formatGroupTotal(subPanelResultsRaw.D_TOTAL),
      A_TOTAL: formatGroupTotal(subPanelResultsRaw.A),
      E_TOTAL: formatGroupTotal(subPanelResultsRaw.E),
    },
    submitted,
    total,
  };
}

export interface FormattedAkroResults {
  otherPoints: {
    bonus?: boolean;
    bonusValue?: number;
    deductions: string;
    judgePointsCorrections: Record<string, unknown>;
    version: number;
  };
  subPanelResults: {
    A_TOTAL: FormattedGroupTotal;
    D_TOTAL: FormattedGroupTotal;
    E_TOTAL: FormattedGroupTotal;
  };
  submitted?: boolean;
  total?: string;
}

export type FormattedAggSubPanelResults = Omit<AggSubPanelResults, "total"> & {
  total: string | null;
};

export function formatOtherJudgesAssessmentsAkro({
  otherPoints: otherPointsRaw,
  subPanelResults: subPanelResultsRaw,
  submitted,
  total: totalRaw,
}: AkroResultsData): FormattedAkroResults {
  const otherPoints = {
    bonus: otherPointsRaw?.bonus,
    bonusValue: otherPointsRaw?.bonusValue,
    deductions: otherPointsRaw?.deductions
      ? formatAssessmentToString(otherPointsRaw?.deductions)
      : DEFAULT.ASSESSMENT,
    judgePointsCorrections: otherPointsRaw?.judgePointsCorrections || {},
    version: otherPointsRaw?.version || 0,
  };
  const total = formatTotal(totalRaw);

  return {
    otherPoints,
    subPanelResults: {
      A_TOTAL: formatGroupTotal(subPanelResultsRaw.A_TOTAL),
      D_TOTAL: formatGroupTotal(subPanelResultsRaw.D_TOTAL),
      E_TOTAL: formatGroupTotal(subPanelResultsRaw.E_TOTAL),
    },
    submitted,
    total,
  };
}

/**
 * @example
 * formatUrl("https://kisanet.fi/api/public/current-performers"): "https://kisanet.fi/api/public/current-performers"
 * formatUrl("https://kisanet.fi////api////public////current-performers"): "https://kisanet.fi/api/public/current-performers"
 */
export function formatUrl(url: string): string {
  const [protocol, pathRaw] = url.split(/:\/{2}/gi);
  const path = pathRaw.replace(/\/{2,}/gi, "/");

  return [protocol, path].join("://");
}

/**
 * @example
 * formatToPx(10): "10px"
 */
export function formatToPx(value: string | number): string {
  return `${value}px`;
}

/**
 * @example
 * formatToMs(10): "10ms"
 */
export function formatToMs(value: string | number): string {
  return `${value}ms`;
}

/**
 * @example
 * formatTotalPointToString(10.40100009): "10,401"
 */
export function formatTotalPointToString(total?: number): string | null {
  return total ? total.toFixed(3).replace(".", ",") : null;
}

/**
 * @example
 * formatAuthorizationCode("10-00-00"): "1000000"
 */
export function formatAuthorizationCode(code: string): string {
  return code.trim().split("-").join("");
}

/**
 * @example
 * formatZeroPenaltyByRules({rule1: 0.0, rule2: 1.1, rule3: 0.1}): {rule2: 1.1, rule3: 0.1}
 */
export function formatZeroPenaltyByRules(penalty?: Record<string, number>): Record<string, number> {
  const formattedPenalty = Object.entries(penalty || {}).filter(
    ([, value]) => value > DEFAULT.ASSESSMENT_FLOAT
  );

  return Object.fromEntries(formattedPenalty);
}
